'use client';

import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { getFaiToken } from '~/api';
import { useAuth } from '~/auth/hooks';
import { Orb } from '~/fai/components';
import { useSize } from '~/hooks';
import { useProfileFromPage } from '~/profile/hooks';

export const Fai = () => {
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const [isFaiSpeaking, setIsFaiSpeaking] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const { profile } = useProfileFromPage();
  const { user } = useAuth();
  const { isMd } = useSize();

  function start() {
    if (isActivating) return;

    setIsActivating(true);
    startSession();
  }

  function stop() {
    if (!isActivating) return;

    setIsActivating(false);
    stopSession();
  }

  async function startSession() {
    const { token } = await getFaiToken({ username: profile?.username });
    const pc = new RTCPeerConnection();

    audioElement.current = document.createElement('audio');
    audioElement.current.autoplay = true;

    pc.ontrack = (e) => {
      const stream = e.streams[0];
      (audioElement as any).current.srcObject = stream;
      monitorAudio(stream); // Start monitoring speech activity
    };

    const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
    pc.addTrack(ms.getTracks()[0]);

    // Set up data channel
    const dc = pc.createDataChannel('oai-events');
    setDataChannel(dc);

    // Start the session using SDP
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const baseUrl = 'https://api.openai.com/v1/realtime';
    const model = 'gpt-4o-realtime-preview-2024-12-17';

    const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
      method: 'POST',
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/sdp',
      },
    });

    await pc.setRemoteDescription({
      type: 'answer',
      sdp: await sdpResponse.text(),
    });

    peerConnection.current = pc;
  }

  function monitorAudio(stream: MediaStream) {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }

    if (audioContextRef.current) {
      const audioContext = audioContextRef.current;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyserRef.current = analyser;
      sourceRef.current = source;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      dataArrayRef.current = dataArray;

      function checkAudioLevel() {
        if (!analyserRef.current || !dataArrayRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        const averageVolume =
          dataArrayRef.current.reduce((sum, val) => sum + val, 0) /
          dataArrayRef.current.length;

        setIsFaiSpeaking(averageVolume > 10); // Adjust threshold as needed
        requestAnimationFrame(checkAudioLevel);
      }

      checkAudioLevel();
    }
  }

  function stopSession() {
    if (dataChannel) {
      dataChannel.close();
    }

    peerConnection.current?.getSenders().forEach((sender) => {
      sender.track?.stop();
    });

    peerConnection.current?.close();
    setIsSessionActive(false);
    setDataChannel(null);
    peerConnection.current = null;
    setIsFaiSpeaking(false); // Ensure speaking state resets

    // Clean up AudioContext
    analyserRef.current?.disconnect();
    sourceRef.current?.disconnect();
    audioContextRef.current?.close();
    analyserRef.current = null;
    sourceRef.current = null;
    audioContextRef.current = null;
  }

  useEffect(() => {
    if (dataChannel) {
      dataChannel.addEventListener('open', () => {
        setIsSessionActive(true);
      });
    }
  }, [dataChannel]);

  useEffect(() => {
    if (isFaiSpeaking) {
      (window as any).targetHover = 1;
    } else {
      (window as any).targetHover = 0;
    }
  }, [isFaiSpeaking]);

  if (user?.plan !== 'admin') return;

  return (
    <motion.div
      className="fixed bottom-2 right-2 z-[100] h-[4.5rem] w-[4.5rem] cursor-pointer rounded-full bg-[#212737] backdrop-blur-md md:bottom-8 md:right-8"
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: 1,
        scale: isMd ? 1 : 0.8,
        transition: {
          type: 'spring',
        },
      }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => {
        if (isSessionActive) {
          stop();
        } else {
          if (isActivating) return;

          start();
        }
      }}
    >
      <AnimatePresence>
        {isSessionActive && (
          <motion.div
            className="absolute left-0 top-0 h-[4.5rem] w-[4.5rem] p-2"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                type: 'spring',
              },
            }}
            exit={{ opacity: 0, scale: 0 }}
          >
            <Orb hoverIntensity={0.5} rotateOnHover={false} hue={30} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!isSessionActive && !isActivating && (
          <motion.div
            className="absolute left-0 top-0 h-[4.5rem] w-[4.5rem] p-2"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                type: 'spring',
              },
            }}
            exit={{ opacity: 0, scale: 0 }}
          >
            <div className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-lg border-4 border-primary" />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!isSessionActive && isActivating && (
          <motion.div
            className="absolute left-0 top-0 h-[4.5rem] w-[4.5rem] p-2"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                type: 'spring',
              },
            }}
            exit={{ opacity: 0, scale: 0 }}
          >
            <div className="absolute left-1/2 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center">
              <div className="loader" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
